import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Phone, Mic, MicOff, Video, VideoOff, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' }
  ]
};

export default function VideoCall() {
  const navigate = useNavigate();
  const { roomId } = useParams();
  const { token, user } = useAuth();
  const [callStatus, setCallStatus] = useState('connecting');
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const timerRef = useRef(null);

  const sendSignal = useCallback(async (signalType, data) => {
    try {
      await axios.post(`${API}/call/signal`, {
        room_id: roomId,
        signal_type: signalType,
        data
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {
      console.error('Failed to send signal:', e);
    }
  }, [roomId, token]);

  const pollSignals = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/call/signals/${roomId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      for (const signal of data.signals) {
        await handleSignal(signal);
      }
    } catch (e) {
      // silently fail
    }
  }, [roomId, token]);

  const handleSignal = async (signal) => {
    const pc = peerConnectionRef.current;
    if (!pc) return;

    if (signal.signal_type === 'offer') {
      await pc.setRemoteDescription(new RTCSessionDescription(signal.data));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await sendSignal('answer', { type: answer.type, sdp: answer.sdp });
    } else if (signal.signal_type === 'answer') {
      await pc.setRemoteDescription(new RTCSessionDescription(signal.data));
    } else if (signal.signal_type === 'ice-candidate' && signal.data) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(signal.data));
      } catch (e) {
        console.error('Failed to add ICE candidate:', e);
      }
    }
  };

  const initializeCall = useCallback(async () => {
    try {
      // Verify room exists
      const { data: roomData } = await axios.get(`${API}/call/room/${roomId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Get local media
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      // Create peer connection
      const pc = new RTCPeerConnection(ICE_SERVERS);
      peerConnectionRef.current = pc;

      // Add local tracks
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      // Handle remote tracks
      pc.ontrack = (event) => {
        if (remoteVideoRef.current && event.streams[0]) {
          remoteVideoRef.current.srcObject = event.streams[0];
          setCallStatus('active');
          // Start call timer
          timerRef.current = setInterval(() => {
            setCallDuration(prev => prev + 1);
          }, 1000);
        }
      };

      // Handle ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          sendSignal('ice-candidate', event.candidate.toJSON());
        }
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
          setCallStatus('ended');
        }
      };

      // If we're the initiator, create an offer
      if (roomData.call.initiator_id === user._id) {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        await sendSignal('offer', { type: offer.type, sdp: offer.sdp });
      }

      setCallStatus('waiting');

      // Start polling for signals
      pollIntervalRef.current = setInterval(pollSignals, 1500);

    } catch (e) {
      console.error('Failed to initialize call:', e);
      if (e.name === 'NotAllowedError') {
        alert('Camera and microphone access is required for video calls');
      }
      setCallStatus('ended');
    }
  }, [roomId, token, user, sendSignal, pollSignals]);

  useEffect(() => {
    initializeCall();
    return () => {
      endCall();
    };
  }, []);

  const toggleMute = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => { track.enabled = !track.enabled; });
      setIsMuted(!isMuted);
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach(track => { track.enabled = !track.enabled; });
      setIsVideoOff(!isVideoOff);
    }
  };

  const endCall = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    if (localStreamRef.current) localStreamRef.current.getTracks().forEach(track => track.stop());
    if (peerConnectionRef.current) peerConnectionRef.current.close();
    setCallStatus('ended');
    navigate(-1);
  };

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="h-screen bg-black flex flex-col relative">
      {/* Remote video */}
      <div className="flex-1 bg-zinc-900 flex items-center justify-center relative">
        {(callStatus === 'connecting' || callStatus === 'waiting') && (
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-rose-500 animate-spin mx-auto mb-4" />
            <p className="text-white font-medium">
              {callStatus === 'connecting' ? 'Connecting...' : 'Waiting for other person...'}
            </p>
            <p className="text-zinc-500 text-sm mt-1">Make sure they open the call link</p>
          </div>
        )}

        {callStatus === 'active' && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-black/50 backdrop-blur-md border border-zinc-700">
            <span className="text-white text-sm font-mono">{formatDuration(callDuration)}</span>
          </div>
        )}

        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className={`w-full h-full object-cover ${callStatus !== 'active' ? 'hidden' : ''}`}
        />
      </div>

      {/* Local video PIP */}
      <div className="absolute top-4 right-4 w-32 h-44 rounded-2xl overflow-hidden bg-zinc-800 shadow-2xl border border-zinc-700">
        <video
          ref={localVideoRef}
          autoPlay
          playsInline
          muted
          className={`w-full h-full object-cover ${isVideoOff ? 'hidden' : ''}`}
        />
        {isVideoOff && (
          <div className="w-full h-full flex items-center justify-center">
            <VideoOff className="w-8 h-8 text-zinc-600" />
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="absolute bottom-0 left-0 right-0 p-8">
        <div className="glass rounded-2xl p-4 max-w-md mx-auto">
          <div className="flex items-center justify-center gap-6">
            <Button
              data-testid="toggle-mute-btn"
              onClick={toggleMute}
              variant="ghost"
              className={`w-14 h-14 rounded-full ${isMuted ? 'bg-red-600/20 text-red-400' : 'bg-zinc-800 text-white'} hover:bg-zinc-700`}
            >
              {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
            </Button>

            <Button
              data-testid="end-call-btn"
              onClick={endCall}
              className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-500"
            >
              <Phone className="w-7 h-7 text-white rotate-135" />
            </Button>

            <Button
              data-testid="toggle-video-btn"
              onClick={toggleVideo}
              variant="ghost"
              className={`w-14 h-14 rounded-full ${isVideoOff ? 'bg-red-600/20 text-red-400' : 'bg-zinc-800 text-white'} hover:bg-zinc-700`}
            >
              {isVideoOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
